import { useMutation } from "@apollo/client";
import { toUpper } from "lodash";
import usePageTitle from "@/hooks/usePageTitle";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { FormValues } from "../_form/validation/schema";
import {
  buildEventFeeInputs,
  getDefaultFormValues,
  getEventTypeInput,
  getRegistrationMaxAttendanceOverride,
  isSeminarFormEvent,
  setToDayBoundary,
  toNumber,
  transformSessionsToInput
} from "../_form/utils";
import { BackofficeEventsCreateEventInput } from "../_form/types";
import { CREATE_EVENT_MUTATION } from "../queries";
import EventForm from "../_form";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { Path } from "@/router";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useState } from "react";
import { useEventsBasePath } from "../_eventsBasePathContext";
import { useReturnTo } from "@/hooks/useSearchParams";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

export default function CreateEventPage() {
  const basePath = useEventsBasePath();
  const returnTo = useReturnTo();
  usePageTitle(`${basePath}/new`, "Events | Create Event");

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const event = location.state?.event;
  const selectedUniversityId =
    (location.state?.universityId as string | undefined) ?? searchParams.get("universityId") ?? undefined;
  const selectedUniversityIds = selectedUniversityId ? [selectedUniversityId] : [];

  useRequireCapability({ domain: DOMAIN.SECTIONS, permission: PERMISSION.manageEvents });
  const [createEventMutation] = useMutation(CREATE_EVENT_MUTATION);
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isCopy = location.state?.isCopy ?? event ?? false;
  const initialValues = {
    ...getDefaultFormValues(event, isCopy),
    ...(selectedUniversityIds.length ? { universityIds: selectedUniversityIds } : {})
  };

  const handleSubmit = async (data: FormValues) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const isPAT = data.eventType.name === "PAT";
      const isRecruitment = data.eventType.name === "Recruitment";
      const isSeminar = isSeminarFormEvent(data);
      const transformedSessions = transformSessionsToInput(
        data.sessions,
        data.timezone,
        getRegistrationMaxAttendanceOverride(data)
      );

      const addressFields =
        data.locationType === "virtual"
          ? {
              address1: "",
              address2: "",
              city: "",
              state: "",
              zip: ""
            }
          : {
              address1: data.address1,
              address2: data.address2,
              city: data.city,
              state: data.state,
              zip: data.zipcode
            };

      const {
        yardageMale,
        courseRatingMale,
        yardageMaleFiftyPlus,
        yardageFemale,
        courseRatingFemale,
        yardageFemaleFiftyPlus,
        hostProfessional,
        examiner
      } = data.patEventAttributes || {};

      const {
        recruitmentEventType,
        purpose,
        strategicFunding,
        pgaRepresentation,
        supportRequested,
        estimatedExpenses,
        sponsorshipAsk,
        estimatedParents,
        estimatedCoaches,
        estimatedOtherAttendees,
        additionalComments,
        eventRecap,
        overallFeedback,
        eventRating,
        commentsAndQuotes,
        photoUploads,
        isFinalized
      } = data.recruitmentEventAttributes || {};

      const registrationFeeCents = data.products?.[0]?.variantPrice;
      const courseFeeCents = data.products?.[1]?.variantPrice;
      const collectionType = data.products?.[1]?.collection;

      const createEventInput: BackofficeEventsCreateEventInput = {
        draft: data.draft,
        bannerImageUrl: data.bannerImageUrl,
        description: data.description,
        title: data.eventName,
        ...getEventTypeInput(data.eventType),
        sectionId: data.sectionId,
        ...(data.universityIds?.length ? { universityIds: data.universityIds } : {}),
        facilityId: data.facilityId,
        location: data.location,
        locationType: toUpper(data.locationType),
        timezone: data.timezone,
        registrationOpenAt: setToDayBoundary(data.registrationOpenDate.startDate, "start", data.timezone),
        registrationCloseAt: setToDayBoundary(data.registrationCloseDate.startDate, "end", data.timezone),
        registrationType: toUpper(data.registrationType),
        sessions: transformedSessions,
        waitlistType: toUpper(data.waitlistType),
        ...addressFields,
        customFields: data.customFields,
        termsContent: data.termsContent,
        collectsAttendance: data.collectsAttendance,
        ...(isSeminar && { eventFees: buildEventFeeInputs(data.eventFees ?? []) }),
        ...(isPAT && {
          patEventAttributes: {
            yardageMale: toNumber(yardageMale),
            courseRatingMale: toNumber(courseRatingMale),
            yardageMaleFiftyPlus: toNumber(yardageMaleFiftyPlus),
            yardageFemale: toNumber(yardageFemale),
            courseRatingFemale: toNumber(courseRatingFemale),
            yardageFemaleFiftyPlus: toNumber(yardageFemaleFiftyPlus),
            hostProfessionalId: hostProfessional?.id,
            examinerId: examiner?.id,
            collectionType: courseFeeCents ? toUpper(collectionType) : undefined,
            registrationFeeCents: registrationFeeCents,
            courseFeeCents: courseFeeCents
          }
        }),
        ...(isRecruitment && {
          recruitmentEventAttributes: {
            recruitmentEventType: recruitmentEventType || "RECRUITMENT_CAREER_FAIR",
            purpose: purpose || "RECRUITING_EVENT",
            strategicFunding: strategicFunding || "NOT_SURE",
            pgaRepresentation: pgaRepresentation || [],
            supportRequested: supportRequested || [],
            estimatedExpenses: toNumber(estimatedExpenses),
            sponsorshipAsk: toNumber(sponsorshipAsk),
            estimatedParents: toNumber(estimatedParents),
            estimatedCoaches: toNumber(estimatedCoaches),
            estimatedOtherAttendees: toNumber(estimatedOtherAttendees),
            additionalComments: additionalComments || "",
            eventRecap: eventRecap || "",
            overallFeedback: overallFeedback || "",
            eventRating: toNumber(eventRating),
            commentsAndQuotes: commentsAndQuotes || "",
            photoUploads: photoUploads || [],
            isFinalized: isFinalized || false
          }
        })
      };

      const { data: responseData } = await createEventMutation({
        variables: { input: createEventInput }
      });
      const { success, event } = responseData.createEvent;
      if (success) {
        if (isRecruitment) {
          navigate(`${basePath}/${event.slug}/recap` as Path);
          showSnackbar("Event submitted", { close: true });
        } else {
          navigate(`${basePath}/${event.slug}/overview` as Path);
          const message = data.draft ? "Event saved as draft" : "Event created";
          showSnackbar(message, { close: true });
        }
        trackEvent(
          EVENT_ANALYTICS_EVENTS.created,
          getEventAnalyticsData({
            source: "create_event",
            eventId: event.id,
            eventSlug: event.slug,
            action: data.draft ? "save_draft" : "create",
            isDraft: !!data.draft,
            isRecruitment
          })
        );
      } else {
        showErrorSnackbar(responseData.createEvent.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EventForm
      onSubmit={handleSubmit}
      initialValues={initialValues}
      isSubmitting={isSubmitting}
      backLinkTo={returnTo || basePath}
    />
  );
}
